import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, Award, Users, Clock } from "lucide-react";

const AboutSection = () => {
  const values = [
    {
      icon: <Heart className="w-8 h-8 text-primary" />,
      title: "Passion",
      description: "Chaque gâteau est créé avec amour et dévouement pour sublimer vos moments précieux"
    },
    {
      icon: <Award className="w-8 h-8 text-primary" />,
      title: "Qualité",
      description: "Ingrédients frais et de première qualité pour des créations qui ravissent les papilles"
    },
    {
      icon: <Users className="w-8 h-8 text-primary" />,
      title: "Sur mesure",
      description: "Chaque création est unique et personnalisée selon vos goûts et votre événement"
    },
    {
      icon: <Clock className="w-8 h-8 text-primary" />,
      title: "Artisanal",
      description: "Fait maison dans notre atelier à Villeurbanne, avec le soin du détail"
    }
  ];

  return (
    <section id="apropos" className="py-20 bg-gradient-to-b from-white to-pink-50/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
              À propos de{" "}
              <span className="gradient-primary bg-clip-text text-transparent">
                Gemima
              </span>
            </h2>
            
            <div className="prose prose-lg text-gray-700 mb-8 max-w-none">
              <p className="text-xl leading-relaxed mb-6">
                Je m'appelle Gemima, cake designer et créatrice de Sweets by Gem. Passionnée depuis toujours 
                par le cake design et la décoration, je réalise des gâteaux faits maison, personnalisés 
                pour chaque événement : anniversaires, mariages, baptêmes, fêtes ou occasions spéciales.
              </p>
              
              <p className="text-lg leading-relaxed mb-6">
                Basée à Villeurbanne, je mets tout mon cœur dans chaque création — du choix des ingrédients 
                jusqu'aux finitions décoratives. Mon objectif : vous offrir un gâteau aussi bon que beau, 
                pour sublimer vos moments les plus précieux.
              </p>
            </div>

            {/* Signature */}
            <div className="bg-gradient-to-r from-pink-50 to-yellow-50 rounded-xl p-6 border border-pink-100 mb-8">
              <div className="text-center">
                <p className="text-lg font-medium text-gray-900 mb-2">
                  ✨ Gemima — Cake Designer & Créatrice à Villeurbanne
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-600">
                  <span>📞 +33 7 53 79 76 98</span>
                  <span className="hidden sm:block">•</span>
                  <span>📧 gemimankdu9@icloud.com</span>
                  <span className="hidden sm:block">•</span>
                  <span>📍 3 rue Magenta, 69100 Villeurbanne</span>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="gradient-primary text-white shadow-elegant"
                onClick={() => {
                  const element = document.querySelector("#contact");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                Réserver votre gâteau
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white"
                onClick={() => window.open("https://www.instagram.com/sweetsby_gem", "_blank")}
              >
                Suivre sur Instagram
              </Button>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-elegant">
              <img
                src="/images/wedding1.jpg"
                alt="Gemima - Cake Designer créatrice de Sweets by Gem"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
            
            {/* Floating quote */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-gold max-w-xs">
              <p className="text-sm text-gray-700 italic mb-2">
                "Chaque gâteau raconte une histoire unique"
              </p>
              <p className="text-xs text-primary font-medium">— Gemima</p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mt-20">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Pourquoi choisir Sweets by Gem ?
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center border-pink-100 hover:shadow-elegant transition-smooth">
                <CardContent className="pt-8 pb-6">
                  <div className="flex justify-center mb-4">
                    {value.icon}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">
                    {value.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Trust Section */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-elegant border border-pink-100 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              💖 Notre engagement qualité
            </h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm text-gray-700">
              <div className="flex flex-col items-center">
                <span className="text-2xl mb-2">✅</span>
                <p><strong>Gâteaux 100% faits maison</strong></p>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-2xl mb-2">🌸</span>
                <p><strong>Ingrédients frais et de qualité</strong></p>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-2xl mb-2">🎨</span>
                <p><strong>Designs personnalisés</strong></p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mt-6 text-sm text-gray-700">
              <div className="flex flex-col items-center">
                <span className="text-2xl mb-2">💌</span>
                <p><strong>Service à l'écoute et réactif</strong></p>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-2xl mb-2">🎁</span>
                <p><strong>Livraison possible sur Lyon</strong></p>
              </div>
            </div>
          </div>
        </div>

        {/* Final Quote */}
        <div className="mt-16 text-center">
          <blockquote className="text-2xl md:text-3xl font-light text-gray-700 italic max-w-3xl mx-auto leading-relaxed">
            "Chez Sweets by Gem, chaque gâteau est une déclaration d'amour sucrée. 
            Nous mettons tout notre cœur dans la réalisation de vos moments les plus doux."
          </blockquote>
          <div className="mt-6 text-primary font-medium">💖</div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;